import { yupResolver } from '@hookform/resolvers/yup';
import type { FormEventHandler } from 'react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { InferType } from 'yup';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { setFormErrors } from 'utils/formHelpers';
import { hasValue } from 'utils/hasValue';

import { ResetPasswordButton } from '@pages/UserManagement/shared/ResetPasswordButton';
import type { User } from 'api';
import { usePutCustomerUsersUserId } from 'api';
import { userProfileEditSchema as schema } from 'validators/userProfileEdit';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { InputField } from 'components/ui/molecules/InputField';

import { LockedPasswordField } from './LockedPasswordField';

type Props = {
  data: User;
  onCancelClick: () => void;
  onSuccess: () => void;
};

interface Inputs extends InferType<typeof schema> {
  base: null;
}
export const UserProfileEdit = ({ data, onCancelClick, onSuccess }: Props) => {
  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();
  const {
    handleSubmit,
    setError,
    register,
    formState: { errors }
  } = useForm<Inputs>({
    mode: 'onSubmit',
    defaultValues: {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email
    },
    resolver: yupResolver(schema)
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const mutation = usePutCustomerUsersUserId();
  const onSubmit: FormEventHandler<HTMLFormElement> = async (event: any) => {
    event.preventDefault();
    try {
      await handleSubmit(async (formData) => {
        await mutation.mutateAsync(
          {
            data: { data: { ...formData } },
            organisationId: organisationId,
            userId: data.id
          },
          {
            onSuccess: () => {
              openNotification({
                kind: 'success',
                message: 'Profile updated'
              });
              onSuccess();
            },
            onError: (error: any) => {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = setTimeout(() => {
                setFormErrors({
                  error,
                  setError,
                  fields: Object.keys(schema.fields)
                });
              }, 50);
            }
          }
        );
      })(event);
    } catch (error: any) {
      console.error('Unexpected error:', error);
    }
  };

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      <div className="flex flex-col px-6 py-4">
        <div className="flex w-1/4 flex-col gap-10">
          <div>
            <label htmlFor="first_name" className="mb-0.5 pl-2 text-xs">
              First Name
            </label>
            <InputField
              {...register('first_name')}
              errorMessage={errors.first_name?.message}
              id="first_name"
            />
          </div>
          <div>
            <label htmlFor="last_name" className="mb-0.5 pl-2 text-xs">
              Last Name
            </label>
            <InputField
              {...register('last_name')}
              errorMessage={errors.last_name?.message}
              id="last_name"
            />
          </div>

          <div>
            <label htmlFor="email" className="mb-0.5 pl-2 text-xs">
              Email
            </label>
            <InputField
              {...register('email')}
              errorMessage={errors.email?.message}
              id="email"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="mb-0.5 pl-2 text-xs text-content-subtle"
            >
              Password
            </label>
            <div className="flex items-center gap-3">
              <LockedPasswordField
                defaultValue="*********************"
                type="password"
                disabled
                className="w-full"
              />
              {hasValue(data.email) ? (
                <ResetPasswordButton email={data.email} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <SecondaryButton size="large" onClick={onCancelClick}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            size="large"
            htmlType="submit"
            disabled={mutation.isPending}
          >
            Save Changes
          </PrimaryButton>
        </div>
      </div>
    </form>
  );
};
